/**
 * Argentinian Holidays Data
 * Includes national holidays and observances that should be excluded from working days calculation
 */

/**
 * Get Argentinian holidays for a given year
 * @param {number} year - The year to get holidays for
 * @returns {Array} Array of Date objects representing holidays
 */
export function getArgentinianHolidays(year) {
  const holidays = [];

  // Fixed date holidays
  const fixedHolidays = [
    { month: 0, day: 1 },   // New Year's Day - Año Nuevo
    { month: 1, day: 14 },  // Valentine's Day (often observed) - Día de San Valentín
    { month: 2, day: 24 },  // Day of Remembrance for Truth and Justice - Día Nacional de la Memoria por la Verdad y la Justicia
    { month: 3, day: 2 },   // Day of the Veterans and Fallen of the Malvinas War - Día del Veterano y de los Caídos en la Guerra de Malvinas
    { month: 4, day: 1 },   // Labour Day - Día del Trabajador
    { month: 4, day: 25 },  // May Revolution - Revolución de Mayo
    { month: 5, day: 17 },  // General Manuel Belgrano Day - Paso a la Inmortalidad del General Manuel Belgrano
    { month: 5, day: 20 },  // Flag Day - Día de la Bandera
    { month: 6, day: 9 },   // Independence Day - Día de la Independencia
    { month: 7, day: 17 },  // General José de San Martín Day - Paso a la Inmortalidad del General José de San Martín  
    { month: 9, day: 12 },  // Day of Respect for Cultural Diversity - Día del Respeto a la Diversidad Cultural
    { month: 10, day: 20 }, // Day of National Sovereignty - Día de la Soberanía Nacional
    { month: 11, day: 8 },  // Immaculate Conception - Inmaculada Concepción de María
    { month: 11, day: 25 }  // Christmas Day - Navidad
  ];

  // Add fixed holidays
  fixedHolidays.forEach(holiday => {
    holidays.push(new Date(year, holiday.month, holiday.day));
  });

  // Calculate Easter-related holidays (variable dates)
  const easter = calculateEaster(year);
  
  // Good Friday (Viernes Santo) - 2 days before Easter
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);
  holidays.push(goodFriday);

  // Carnival Monday (Lunes de Carnaval) - 48 days before Easter
  const carnivalMonday = new Date(easter);
  carnivalMonday.setDate(easter.getDate() - 48);
  holidays.push(carnivalMonday);

  // Carnival Tuesday (Martes de Carnaval) - 47 days before Easter
  const carnivalTuesday = new Date(easter);
  carnivalTuesday.setDate(easter.getDate() - 47);
  holidays.push(carnivalTuesday);

  // Bridge holidays (feriados puente) - these vary by year and government decree
  // Adding common bridge holidays for recent years
  const bridgeHolidays = getBridgeHolidays(year);
  holidays.push(...bridgeHolidays);

  return holidays.sort((a, b) => a - b);
}

/**
 * Calculate Easter Sunday for a given year using the algorithm
 * @param {number} year - The year to calculate Easter for
 * @returns {Date} Easter Sunday date
 */
function calculateEaster(year) {
  // Using the anonymous Gregorian algorithm
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const n = Math.floor((h + l - 7 * m + 114) / 31);
  const p = (h + l - 7 * m + 114) % 31;
  
  return new Date(year, n - 1, p + 1);
}

/**
 * Get bridge holidays for a specific year
 * Bridge holidays are typically declared to create long weekends
 * @param {number} year - The year to get bridge holidays for
 * @returns {Array} Array of Date objects representing bridge holidays
 */
function getBridgeHolidays(year) {
  const bridges = [];
  
  // Common bridge patterns (these change each year by government decree)
  // Adding some common ones for recent years as examples
  
  if (year === 2024) {
    bridges.push(
      new Date(2024, 3, 1),   // April 1 (bridge for Easter)
      new Date(2024, 4, 24),  // May 24 (bridge for May 25)
      new Date(2024, 5, 21),  // June 21 (bridge for Flag Day)
      new Date(2024, 9, 11),  // October 11 (bridge for Cultural Diversity Day)
      new Date(2024, 10, 18)  // November 18 (bridge for Sovereignty Day)
    );
  }
  
  if (year === 2025) {
    bridges.push(
      new Date(2025, 3, 18),  // April 18 (bridge for Easter)
      new Date(2025, 4, 2),   // May 2 (bridge for Labour Day)
      new Date(2025, 5, 16),  // June 16 (bridge for Belgrano Day)
      new Date(2025, 7, 18),  // August 18 (bridge for San Martín Day)
      new Date(2025, 9, 13),  // October 13 (bridge for Cultural Diversity Day)
      new Date(2025, 10, 21)  // November 21 (bridge for Sovereignty Day)
    );
  }

  if (year === 2026) {
    bridges.push(
      new Date(2026, 2, 23),  // March 23 (bridge for Truth and Justice Day)
      new Date(2026, 5, 15),  // June 15 (bridge for Belgrano Day)
      new Date(2026, 6, 10),  // July 10 (bridge for Independence Day)
      new Date(2026, 9, 9),   // October 9 (bridge for Cultural Diversity Day)
      new Date(2026, 11, 7)   // December 7 (bridge for Immaculate Conception)
    );
  }
  
  return bridges;
}

/**
 * Check if a given date is an Argentinian holiday
 * @param {Date} date - The date to check
 * @returns {boolean} True if the date is a holiday
 */
export function isArgentinianHoliday(date) {
  const year = date.getFullYear();
  const holidays = getArgentinianHolidays(year);
  
  return holidays.some(holiday => 
    holiday.getFullYear() === date.getFullYear() &&
    holiday.getMonth() === date.getMonth() &&
    holiday.getDate() === date.getDate()
  );
}

/**
 * Get all holidays for a date range
 * @param {Date} startDate - Start date of the range
 * @param {Date} endDate - End date of the range
 * @returns {Array} Array of holidays within the range
 */
export function getHolidaysInRange(startDate, endDate) {
  const holidays = [];
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  
  // Get holidays for all years in the range
  for (let year = startYear; year <= endYear; year++) {
    const yearHolidays = getArgentinianHolidays(year);
    yearHolidays.forEach(holiday => {
      if (holiday >= startDate && holiday <= endDate) {
        holidays.push(holiday);
      }
    });
  }
  
  return holidays.sort((a, b) => a - b);
}

/**
 * Format a holiday date for display
 * @param {Date} date - The holiday date
 * @returns {string} Formatted date string
 */
export function formatHoliday(date) {
  return date.toLocaleDateString('es-AR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Get holiday names (optional - for debugging/display purposes)
 * @param {Date} date - The holiday date
 * @returns {string|null} Holiday name if found
 */
export function getHolidayName(date) {
  const month = date.getMonth();
  const day = date.getDate();
  
  const holidayNames = {
    '0-1': 'Año Nuevo',
    '1-14': 'Día de San Valentín',
    '2-24': 'Día Nacional de la Memoria por la Verdad y la Justicia',
    '3-2': 'Día del Veterano y de los Caídos en la Guerra de Malvinas',
    '4-1': 'Día del Trabajador',
    '4-25': 'Revolución de Mayo',
    '5-17': 'Paso a la Inmortalidad del General Manuel Belgrano',
    '5-20': 'Día de la Bandera',
    '6-9': 'Día de la Independencia',
    '7-17': 'Paso a la Inmortalidad del General José de San Martín',
    '9-12': 'Día del Respeto a la Diversidad Cultural',
    '10-20': 'Día de la Soberanía Nacional',
    '11-8': 'Inmaculada Concepción de María',
    '11-25': 'Navidad'
  };
  
  const key = `${month}-${day}`;
  return holidayNames[key] || null;
}
